import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Clock, Plus, Printer, ShieldCheck, Ticket } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import {
  EXCHANGE_REASON_CODES,
  EXCHANGE_REASON_LABELS,
  NO_REASON_LABEL,
  type ExchangeDetail,
  type ExchangeReasonCode,
  type ExchangeSort,
  type ExchangeSummary,
} from '@aeris/contracts'
import { MONEY_SCALE, formatMoney, fromString } from '@aeris/money'
import type { PaperWidth } from '@aeris/printing'
import { formatDocumentNumber } from '@aeris/printing'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  InlineAlert,
  ListToolbar,
  LoadFailed,
  Page,
  PageHeader,
  Pagination,
  QueryState,
  ResultCount,
  Section,
  Select,
  SortableHeaderCell,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Textarea,
  VisuallyHidden,
} from '@aeris/ui'

import { ExchangeSlipDialog } from '../components/exchange-slip-dialog.tsx'
import { ExportMenu } from '../components/export-menu.tsx'
import { NewExchangeDialog } from '../components/new-exchange-dialog.tsx'
import { OldManagementAccount } from '../components/old-management-account.tsx'
import { OutsideExchangeDialog } from '../components/outside-exchange-dialog.tsx'
import { PeriodPicker } from '../components/period-picker.tsx'
import { fetchBrands } from '../lib/api/brands.ts'
import {
  fetchExchange,
  fetchExchanges,
  rejectExchangeRequest,
} from '../lib/api/exchanges.ts'
import { ApiError } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { EXCHANGE_EXPORT } from '../lib/exchange-export.ts'
import {
  customRange,
  exclusiveEnd,
  periodRange,
  type PeriodChoice,
} from '../lib/period-range.ts'
import { readRememberedPaper, rememberPaper } from '../lib/remembered-paper.ts'
import { documentRef } from '../lib/sales-labels.ts'
import { timeAgo } from '../lib/time-ago.ts'
import { useTableSort } from '../lib/use-table-sort.ts'

/**
 * A troca e o vale — F126, fatia 2b.
 *
 * O trocador atende direto: acha a venda, marca o que voltou e se volta ao
 * estoque, escolhe o que o cliente leva (ou não leva nada, e é vale), confere o
 * total e conclui. O comprovante abre sozinho, porque "vem impresso na térmica"
 * foi o pedido.
 *
 * ## O que a tela calcula e o que ela não decide
 *
 * O rodapé mostra devolvido, levado, e o que vira vale ou "a pagar no caixa" —
 * com a mesma aritmética de `@aeris/money` que o servidor usa. Mas o preço que
 * vale é o que o SERVIDOR lê: o da venda original para o que voltou, o da
 * tabela padrão para o que sai. Se os dois discordarem, quem manda é o
 * comprovante que o servidor devolve.
 */

const money = (wire: string, scale = MONEY_SCALE) =>
  formatMoney(fromString(wire, scale))

const dateFormat = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
})

const RejectRequestDialog = ({
  request,
  onDone,
  onClose,
}: {
  request: ExchangeSummary
  onDone: () => void
  onClose: () => void
}): ReactNode => {
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const reject = useMutation({
    mutationFn: async () =>
      await rejectExchangeRequest(request.id, reason.trim()),
    onSuccess: () => {
      onDone()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível recusar o pedido.'
      )
    },
  })

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        title="Recusar pedido"
        description={`${documentRef(request.originalSaleKind, request.originalSaleNumber)} · ${request.customerName}. O caixa vê o motivo.`}
      >
        <label className="flex flex-col gap-1">
          <span className="text-(length:--text-sm)">Motivo</span>
          <Textarea
            value={reason}
            rows={3}
            placeholder="ex.: fora do prazo de troca, peça com mau uso"
            onChange={(event) => {
              setReason(event.target.value)
            }}
          />
        </label>
        {error === null ? null : (
          <p role="alert" className="text-(length:--text-sm) text-(--danger)">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            variant="primary"
            disabled={reason.trim() === ''}
            loading={reject.isPending}
            onClick={() => {
              setError(null)
              reject.mutate()
            }}
          >
            Recusar pedido
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const PAGE_SIZE = 25

/** A exportação caminha de 100 em 100. */
const EXPORT_PAGE_SIZE = 100

export const TrocasPage = (): ReactNode => {
  const queryClient = useQueryClient()
  const { allows } = useAuth()
  const canManage = allows('exchange.manage')

  const [creating, setCreating] = useState(false)
  /** A troca de produto de fora, com a senha do admin (F148). */
  const [outside, setOutside] = useState(false)
  const [attending, setAttending] = useState<ExchangeSummary | null>(null)
  const [rejecting, setRejecting] = useState<ExchangeSummary | null>(null)
  const [printing, setPrinting] = useState<ExchangeDetail | null>(null)
  const [paper, setPaper] = useState<PaperWidth>(readRememberedPaper)
  const [opening, setOpening] = useState<string | null>(null)

  // Paginado (F132): antes a lista parava em 50 trocas sem dizer.
  const [page, setPage] = useState(1)

  /*
   * O período — F158, e ele faltava.
   *
   * A rota nasceu sem filtro de data, e o buraco só apareceu quando a
   * exportação chegou: o arquivo levava TODAS as trocas concluídas da história
   * da loja, mesmo com a tela mostrando "as últimas 25". Agora a tela escolhe o
   * período e o arquivo leva exatamente ele.
   */
  // Nulo é "Qualquer data" (F161: o dono pediu o calendário aqui).
  const [period, setPeriod] = useState<PeriodChoice | null>(null)
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')

  /*
   * O motivo e a marca — F188.
   *
   * O dono: *"tem que ter o motivo sempre, pode filtrar por motivo, por
   * marca"*. O Eduardo precisa do porquê de cada peça para levar ao
   * fornecedor, e até aqui a tela não mostrava motivo nenhum.
   */
  const [reasonCode, setReasonCode] = useState('')
  const [brand, setBrand] = useState('')
  /*
   * Da loja ou da gestão antiga — F216. O dono: não dá para ver o que é troca
   * de venda deles e o que é de cliente da loja anterior.
   */
  const [origin, setOrigin] = useState<'' | 'store' | 'old'>('')
  const exchangeSort = useTableSort<ExchangeSort>('when', 'desc', () => {
    setPage(1)
  })

  /*
   * O filtro que está na tela, sem a página — lido pela lista E pela
   * exportação, que é o que impede o arquivo de discordar do que se vê.
   */
  const listRequest = useMemo(() => {
    const range =
      period === null
        ? null
        : period === 'custom'
          ? customRange(customFrom, customTo)
          : periodRange(period)
    return {
      status: 'completed' as const,
      ...(range === null
        ? {}
        : {
            completedFrom: range.from,
            // Fim EXCLUSIVO, como o painel: `periodRange` devolve o último
            // instante do dia, e a rota compara com `<`.
            completedTo: exclusiveEnd(range.to),
          }),
      ...(reasonCode === ''
        ? {}
        : { reasonCode: reasonCode as ExchangeReasonCode | 'sem-motivo' }),
      ...(brand === '' ? {} : { brand }),
      ...(origin === '' ? {} : { origin }),
      sort: exchangeSort.sort,
      direction: exchangeSort.direction,
    }
  }, [
    period,
    customFrom,
    customTo,
    reasonCode,
    brand,
    origin,
    exchangeSort.sort,
    exchangeSort.direction,
  ])

  /*
   * As marcas do filtro — F188. A lista inteira de uma vez é o que o próprio
   * cadastro já faz (uma loja tem dezenas de marcas, não cem mil), e é a mesma
   * consulta que o Estoque e Produtos usam, então o cache é compartilhado.
   */
  const brandQuery = useQuery({
    queryKey: ['brands', 'active'],
    queryFn: ({ signal }) => fetchBrands('active', signal),
  })
  const brands = (brandQuery.data ?? []).map((entry) => entry.name)

  const list = useQuery({
    queryKey: ['exchanges', 'completed', listRequest, page],
    queryFn: async ({ signal }) =>
      await fetchExchanges(
        { ...listRequest, page, pageSize: PAGE_SIZE },
        signal
      ),
    placeholderData: (previous) => previous,
  })

  const reprint = useMutation({
    // O erro aparece na própria tela; o aviso geral não repete (F248).

    meta: { errorShownInline: true },
    mutationFn: async (id: string) => await fetchExchange(id),
    onMutate: (id) => {
      setOpening(id)
    },
    onSuccess: (response) => {
      setPrinting(response.exchange)
    },
    onSettled: () => {
      setOpening(null)
    },
  })

  /*
   * Os pedidos do caixa (fatia 2d), mais antigos primeiro na fila de quem
   * atende — a lista vem ordenada pela data, e o trocador lê de baixo.
   */
  const pending = useQuery({
    queryKey: ['exchanges', 'requested'],
    queryFn: async ({ signal }) =>
      await fetchExchanges({ status: 'requested', pageSize: 50 }, signal),
  })
  const pendingItems = pending.data?.items ?? []

  /*
   * Os recusados recentes, com o motivo.
   *
   * O diálogo de recusa diz que o caixa vê o motivo — e é aqui que ele vê.
   * Sem esta lista, um pedido recusado sumiria da fila sem explicação, e o
   * caixa pediria de novo achando que se perdeu.
   */
  const rejected = useQuery({
    queryKey: ['exchanges', 'rejected'],
    queryFn: async ({ signal }) =>
      await fetchExchanges({ status: 'rejected', pageSize: 10 }, signal),
  })
  const rejectedItems = rejected.data?.items ?? []

  const items = list.data?.items ?? []
  const total = list.data?.total ?? 0
  const pendingTotal = pending.data?.total ?? 0

  return (
    <Page>
      <PageHeader
        title="Trocas e vales"
        description="O que voltou para a loja, o que saiu no lugar, e o vale que ficou para o cliente."
        actions={
          canManage ? (
            <div className="flex flex-wrap gap-2">
              {/* F148: o cliente da loja anterior, sem venda nossa. Pede a
                  senha de um admin, gerente ou dono. */}
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={
                  <ShieldCheck aria-hidden="true" className="h-4 w-4" />
                }
                onClick={() => {
                  setOutside(true)
                }}
              >
                Produto de fora (sem venda)
              </Button>
              <Button
                variant="primary"
                size="sm"
                leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
                onClick={() => {
                  setCreating(true)
                }}
              >
                Nova troca ou vale
              </Button>
            </div>
          ) : null
        }
      />

      {/*
        Três blocos com o mesmo cabeçalho e os mesmos quatro estados (F132).
        Antes cada um tinha o seu jeito de dizer que não carregou — um texto
        solto, um texto sem "tentar de novo" e um cartão.
      */}
      <Section
        title="Pedidos do caixa"
        description={
          pending.data === undefined
            ? undefined
            : pendingTotal === 0
              ? 'Nenhum pedido esperando.'
              : pendingTotal === 1
                ? '1 pedido esperando, o mais antigo primeiro.'
                : `${String(pendingTotal)} pedidos esperando, o mais antigo primeiro.`
        }
      >
        <QueryState
          isPending={pending.isPending}
          isError={pending.isError}
          error={pending.error}
          onRetry={() => {
            void pending.refetch()
          }}
          what="os pedidos do caixa"
          isEmpty={pendingItems.length === 0}
          empty={null}
        >
          {/*
            Cartões, e não tabela (F136): quem atende lê "quem, o que quer, por
            quê e há quanto tempo está esperando" de relance, e o motivo
            inteiro cabe — na tabela ele era uma célula cortada.
          */}
          <ul
            aria-label="Pedidos do caixa"
            className="grid gap-2 md:grid-cols-2"
          >
            {pendingItems.map((request) => {
              const waited = timeAgo(request.requestedAt ?? request.createdAt)
              return (
                <li
                  key={request.id}
                  className="flex flex-col gap-2 rounded-(--radius-md) border border-(--border-default) bg-(--surface) p-3"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex min-w-0 flex-col">
                      <span className="font-(--weight-medium) text-(--text-primary)">
                        {request.customerName}
                      </span>
                      <span className="text-(length:--text-xs) text-(--text-muted)">
                        {`${documentRef(request.originalSaleKind, request.originalSaleNumber)} · pedido por ${request.requestedByName ?? '—'}`}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge
                        tone={request.kind === 'voucher' ? 'info' : 'neutral'}
                      >
                        {request.kind === 'voucher' ? 'Vale' : 'Troca'}
                      </Badge>
                      <Badge
                        tone="warning"
                        icon={<Clock aria-hidden="true" className="h-3 w-3" />}
                      >
                        {waited === 'agora'
                          ? 'chegou agora'
                          : `esperando ${waited.replace('há ', '')}`}
                      </Badge>
                    </div>
                  </div>
                  {/*
                    O motivo na fila — F158.
                    
                    O código primeiro, porque agora é ele que sempre existe: o
                    texto virou detalhe opcional, e um cartão que só mostrasse a
                    frase ficaria mudo justamente nos pedidos feitos com um
                    toque. O trocador escolhe qual pedido pegar por isto.
                  */}
                  {request.reasonCode === null &&
                  request.reason === null ? null : (
                    <p className="text-(length:--text-sm) text-(--text-secondary)">
                      {request.reasonCode === null
                        ? null
                        : EXCHANGE_REASON_LABELS[request.reasonCode]}
                      {request.reason === null
                        ? null
                        : `${request.reasonCode === null ? '' : ' · '}“${request.reason}”`}
                    </p>
                  )}
                  {canManage ? (
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setRejecting(request)
                        }}
                      >
                        Recusar
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          setAttending(request)
                        }}
                      >
                        Atender
                      </Button>
                    </div>
                  ) : null}
                </li>
              )
            })}
          </ul>
        </QueryState>
      </Section>

      {rejected.isError ? (
        <LoadFailed
          what="os pedidos recusados"
          onRetry={() => {
            void rejected.refetch()
          }}
        />
      ) : rejectedItems.length === 0 ? null : (
        <Section
          title="Recusados recentemente"
          description="O motivo que o caixa vê quando o pedido dele não foi atendido."
        >
          <ul className="flex flex-col gap-1 text-(length:--text-sm)">
            {rejectedItems.map((item) => (
              <li key={item.id} className="text-(--text-secondary)">
                {documentRef(item.originalSaleKind, item.originalSaleNumber)} ·{' '}
                {item.customerName} — recusado por {item.handledByName ?? '—'}:{' '}
                <strong className="text-(--text-primary)">
                  {item.rejectionReason ?? '—'}
                </strong>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {allows('report.read') ? <OldManagementAccount /> : null}

      <Section
        title="Concluídas"
        /*
          F158. Leva as trocas concluídas inteiras, não a página — com o motivo,
          a observação e o motivo da recusa, que não cabem na tabela.
        */
        action={
          <ListToolbar
            actions={
              <ExportMenu
                fileName="trocas"
                sheetName="Trocas"
                columns={EXCHANGE_EXPORT}
                fetchPage={async (exportPage) =>
                  await fetchExchanges({
                    ...listRequest,
                    page: exportPage,
                    pageSize: EXPORT_PAGE_SIZE,
                  })
                }
                pageSize={EXPORT_PAGE_SIZE}
                total={total}
              />
            }
          >
            <PeriodPicker
              choice={period ?? 'today'}
              onChoiceChange={(choice) => {
                setPeriod(choice)
                setPage(1)
              }}
              anyDate={{
                selected: period === null,
                onSelect: () => {
                  setPeriod(null)
                  setPage(1)
                },
              }}
              customFrom={customFrom}
              customTo={customTo}
              onCustomFromChange={(day) => {
                setCustomFrom(day)
                setPage(1)
              }}
              onCustomToChange={(day) => {
                setCustomTo(day)
                setPage(1)
              }}
            />
            <Field label="Origem">
              {(field) => (
                <Select
                  {...field}
                  value={origin}
                  onChange={(event) => {
                    const value = event.target.value
                    setOrigin(value === 'store' || value === 'old' ? value : '')
                    setPage(1)
                  }}
                >
                  <option value="">Loja e gestão antiga</option>
                  <option value="store">Da loja (venda do Aeris)</option>
                  <option value="old">Da gestão antiga</option>
                </Select>
              )}
            </Field>
            <Field label="Motivo">
              {(field) => (
                <Select
                  {...field}
                  value={reasonCode}
                  onChange={(event) => {
                    setReasonCode(event.target.value)
                    setPage(1)
                  }}
                >
                  <option value="">Qualquer motivo</option>
                  {EXCHANGE_REASON_CODES.map((code) => (
                    <option key={code} value={code}>
                      {EXCHANGE_REASON_LABELS[code]}
                    </option>
                  ))}
                  {/*
                    Metade das peças da Elite está assim, e é justamente essa
                    metade que o dono precisa achar para ir atrás — por isso a
                    falta de motivo é uma opção, não a ausência do filtro.
                  */}
                  <option value="sem-motivo">{NO_REASON_LABEL}</option>
                </Select>
              )}
            </Field>
            {brands.length === 0 ? null : (
              <Field label="Marca">
                {(field) => (
                  <Select
                    {...field}
                    value={brand}
                    onChange={(event) => {
                      setBrand(event.target.value)
                      setPage(1)
                    }}
                  >
                    <option value="">Todas as marcas</option>
                    {brands.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
          </ListToolbar>
        }
      >
        <ResultCount
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          loading={list.isPending}
          none="Nenhuma troca concluída"
        />

        <QueryState
          isPending={list.isPending}
          isError={list.isError}
          error={list.error}
          onRetry={() => {
            void list.refetch()
          }}
          what="as trocas"
          isEmpty={items.length === 0}
          /*
           * Com período, motivo, marca ou origem ligados, o vazio é resposta ao
           * filtro (F248) — e não "Nenhuma troca ainda", que manda a pessoa
           * fazer a primeira troca de uma loja que já tem centenas.
           */
          filtered={
            period !== null ||
            reasonCode !== '' ||
            brand !== '' ||
            origin !== ''
          }
          onClearFilters={() => {
            setPeriod(null)
            setReasonCode('')
            setBrand('')
            setOrigin('')
            setPage(1)
          }}
          empty={
            <EmptyState
              icon={<Ticket aria-hidden="true" className="h-8 w-8" />}
              title="Nenhuma troca ainda"
              description={
                canManage
                  ? 'Comece por "Nova troca ou vale": busque a venda, marque o que voltou e conclua.'
                  : 'Quando o trocador concluir uma troca, ela aparece aqui.'
              }
            />
          }
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableHeaderCell {...exchangeSort.column('number')}>
                    Nº
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    kind="date"
                    {...exchangeSort.column('when')}
                  >
                    Quando
                  </SortableHeaderCell>
                  <SortableHeaderCell {...exchangeSort.column('customer')}>
                    Cliente
                  </SortableHeaderCell>
                  <SortableHeaderCell {...exchangeSort.column('kind')}>
                    Tipo
                  </SortableHeaderCell>
                  {/*
                    A coluna que o dono pediu — F188. Antes o motivo existia no
                    banco, saía no arquivo exportado e aparecia no comprovante,
                    e era a única coisa que a LISTA não mostrava.
                  */}
                  <SortableHeaderCell {...exchangeSort.column('reason')}>
                    Motivo
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    numeric
                    {...exchangeSort.column('returnedTotal')}
                  >
                    Devolvido
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    numeric
                    {...exchangeSort.column('voucherCredit')}
                  >
                    Vale
                  </SortableHeaderCell>
                  <SortableHeaderCell {...exchangeSort.column('handledBy')}>
                    Atendido por
                  </SortableHeaderCell>
                  <TableHeaderCell>
                    <VisuallyHidden>Ações</VisuallyHidden>
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((exchange) => (
                  <TableRow key={exchange.id}>
                    <TableCell className="font-(--font-mono)">
                      {formatDocumentNumber(exchange.number)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-(--text-secondary)">
                      {dateFormat.format(
                        new Date(exchange.completedAt ?? exchange.createdAt)
                      )}
                    </TableCell>
                    <TableCell>
                      {exchange.customerName}
                      {exchange.originalSaleId === null ? (
                        <>
                          {' '}
                          <Badge tone="warning">Gestão antiga</Badge>
                        </>
                      ) : null}
                      <span className="block text-(length:--text-xs) text-(--text-muted)">
                        {exchange.originalSaleId === null
                          ? `produto de fora · autorizado por ${exchange.authorisedByName ?? '—'}`
                          : `venda nº ${String(exchange.originalSaleNumber ?? '—')}`}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        tone={exchange.kind === 'voucher' ? 'info' : 'neutral'}
                      >
                        {exchange.kind === 'voucher' ? 'Vale' : 'Troca'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {exchange.reasonCode === null ? (
                        /*
                          Nem traço nem "Outro": um traço é mudo e "Outro" é
                          uma escolha que ninguém fez. Em cinza, porque é a
                          falta de um dado — e ela precisa ser visível para
                          alguém querer consertá-la.
                        */
                        <span className="text-(--text-muted) italic">
                          {NO_REASON_LABEL}
                        </span>
                      ) : (
                        EXCHANGE_REASON_LABELS[exchange.reasonCode]
                      )}
                      {/* A frase do balcão, quando o trocador escreveu uma. */}
                      {exchange.reason === null ? null : (
                        <span className="block text-(length:--text-xs) text-(--text-muted)">
                          {exchange.reason}
                        </span>
                      )}
                    </TableCell>
                    <TableCell numeric>
                      {money(exchange.returnedTotal)}
                    </TableCell>
                    <TableCell numeric>
                      {money(exchange.voucherCreditAmount)}
                      {/*
                        O que abateu no carnê, dito ao lado do vale (F159).

                        Sem isto, uma troca de R$ 100 numa venda a prazo aparece
                        com "Vale R$ 0,00" — e quem lê conclui que a devolução
                        não deu nada ao cliente, quando ela quitou a dívida
                        dele. É o mesmo dinheiro, por outro caminho.
                      */}
                      {Number(exchange.creditAppliedAmount) === 0 ? null : (
                        <span className="block text-(length:--text-xs) text-(--text-secondary)">
                          {money(exchange.creditAppliedAmount)} no carnê
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-(--text-secondary)">
                      {exchange.handledByName ?? '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={opening === exchange.id}
                        leadingIcon={
                          <Printer aria-hidden="true" className="h-3.5 w-3.5" />
                        }
                        onClick={() => {
                          reprint.mutate(exchange.id)
                        }}
                      >
                        Comprovante
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </QueryState>

        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      </Section>

      {reprint.isError ? (
        <InlineAlert tone="danger">
          Não foi possível abrir o comprovante. Tente de novo.
        </InlineAlert>
      ) : null}

      {rejecting === null ? null : (
        <RejectRequestDialog
          request={rejecting}
          onClose={() => {
            setRejecting(null)
          }}
          onDone={() => {
            setRejecting(null)
            void queryClient.invalidateQueries({ queryKey: ['exchanges'] })
          }}
        />
      )}

      {outside ? (
        <OutsideExchangeDialog
          onClose={() => {
            setOutside(false)
          }}
          onDone={(exchange) => {
            setOutside(false)
            setPrinting(exchange)
            void queryClient.invalidateQueries({ queryKey: ['exchanges'] })
            void queryClient.invalidateQueries({
              queryKey: ['customer-credit', exchange.customerPartyId],
            })
          }}
        />
      ) : null}

      {creating || attending !== null ? (
        <NewExchangeDialog
          request={attending}
          onClose={() => {
            setCreating(false)
            setAttending(null)
          }}
          onDone={(exchange) => {
            setCreating(false)
            setAttending(null)
            setPrinting(exchange)
            void queryClient.invalidateQueries({ queryKey: ['exchanges'] })
            void queryClient.invalidateQueries({
              queryKey: ['customer-credit', exchange.customerPartyId],
            })
          }}
        />
      ) : null}

      {printing === null ? null : (
        <ExchangeSlipDialog
          exchange={printing}
          paper={paper}
          onPaperChange={(next) => {
            setPaper(next)
            rememberPaper(next)
          }}
          onClose={() => {
            setPrinting(null)
          }}
        />
      )}
    </Page>
  )
}
